// <invoice-form> — the live maths behind "What you will be paid" on the
// portal's submit/edit form, plus line add/remove/renumber. Markup
// convention (rendered by Views::Portal::Invoices::Form):
//
//   <invoice-form data-cis-rate="20" data-vat-rate="20" data-reverse-charge="true">
//     <form …>
//       <div data-section="labour">
//         <div data-lines="labour">
//           <div data-line>
//             <input type="hidden" name="…[_destroy]" data-destroy>
//             <span data-line-number>1</span>
//             <input name="…[amount]" data-amount>
//             <button data-remove-line>
//           </div>
//         </div>
//         <template data-line-template="labour">…__INDEX__…</template>
//         <button data-add-line="labour">
//       </div>
//       … materials section, same shape …
//       <span data-total-for="labour|materials|vat|cis|to-pay">
//       <button data-submit-button>
//     </form>
//   </invoice-form>
//
// The maths mirrors the server exactly (Invoice#recalculate_totals): work
// in integer pence, CIS floors and applies to labour only, VAT shows on
// the invoice but never joins the amount to pay (domestic reverse charge).
// Progressive enhancement: without JS the server-rendered rows submit and
// the server does the same sums.

class InvoiceForm extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.addEventListener(
			"input",
			(event) => {
				if (
					event.target instanceof HTMLElement &&
					"amount" in event.target.dataset
				) {
					this.#recalculate();
				}
			},
			{ signal },
		);

		this.addEventListener(
			"click",
			(event) => {
				const target = event.target;
				if (!(target instanceof Element)) return;

				const add = target.closest<HTMLElement>("[data-add-line]");
				if (add?.dataset.addLine) {
					this.#addLine(add.dataset.addLine);
					return;
				}

				if (target.closest("[data-remove-line]")) {
					const line = target.closest<HTMLElement>("[data-line]");
					if (line) this.#removeLine(line);
				}
			},
			{ signal },
		);

		this.#recalculate();
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	#addLine(section: string) {
		const template = this.querySelector<HTMLTemplateElement>(
			`[data-line-template="${section}"]`,
		);
		const list = this.querySelector<HTMLElement>(`[data-lines="${section}"]`);
		if (!template || !list) return;

		// A timestamp index keeps every lines_attributes key unique without
		// the element having to track a counter across both sections.
		const html = template.innerHTML.replaceAll(
			"__INDEX__",
			Date.now().toString(),
		);
		list.insertAdjacentHTML("beforeend", html);

		this.#renumber(section);
		list
			.querySelector<HTMLTextAreaElement>("[data-line]:last-child textarea")
			?.focus();
	}

	#removeLine(line: HTMLElement) {
		const section = line.closest<HTMLElement>("[data-lines]")?.dataset.lines;
		const destroy = line.querySelector<HTMLInputElement>("[data-destroy]");
		const persisted = line.querySelector<HTMLInputElement>(
			'input[name$="[id]"]',
		);

		if (persisted && destroy) {
			// Persisted rows can't just vanish from the DOM — the server needs
			// the _destroy flag to delete them. Hide, flag, and zero the
			// amount so the live totals agree with what will be saved.
			destroy.value = "1";
			line.hidden = true;
			const amount = line.querySelector<HTMLInputElement>("[data-amount]");
			if (amount) amount.value = "";
		} else {
			line.remove();
		}

		if (section) this.#renumber(section);
		this.#recalculate();
	}

	#renumber(section: string) {
		const rows = this.querySelectorAll<HTMLElement>(
			`[data-lines="${section}"] [data-line]:not([hidden])`,
		);
		rows.forEach((row, index) => {
			const number = row.querySelector("[data-line-number]");
			if (number) number.textContent = (index + 1).toString();
		});
	}

	#recalculate() {
		const labour = this.#sectionPence("labour");
		const materials = this.#sectionPence("materials");

		const cisRate = Number(this.dataset.cisRate ?? 0);
		const vatRate = Number(this.dataset.vatRate ?? 0);

		// Integer division floors the deduction to the penny — the
		// HMRC-friendly direction, and what the server does.
		const cis = Math.floor((labour * cisRate) / 100);
		const vat = Math.floor(((labour + materials) * vatRate) / 100);
		const toPay = labour + materials - cis;

		this.#setTotal("labour", labour);
		this.#setTotal("materials", materials);
		this.#setTotal("vat", vat);
		this.#setTotal("cis", cis);
		this.#setTotal("to-pay", toPay);

		// Nothing to pay means nothing to submit — mirrors the mockup's
		// dimmed button. Server validation remains the real gate.
		const submit = this.querySelector<HTMLButtonElement>(
			"[data-submit-button]",
		);
		if (submit) submit.disabled = toPay <= 0;
	}

	#sectionPence(section: string): number {
		let total = 0;
		const amounts = this.querySelectorAll<HTMLInputElement>(
			`[data-lines="${section}"] [data-line]:not([hidden]) [data-amount]`,
		);
		for (const input of amounts) {
			const value = Number.parseFloat(input.value.replace(/[£,\s]/g, ""));
			if (!Number.isNaN(value) && value > 0) total += Math.round(value * 100);
		}
		return total;
	}

	#setTotal(key: string, pence: number) {
		const target = this.querySelector(`[data-total-for="${key}"]`);
		if (target) target.textContent = formatGbp(pence);
	}
}

function formatGbp(pence: number): string {
	const sign = pence < 0 ? "\u2212" : "";
	const pounds = Math.floor(Math.abs(pence) / 100).toLocaleString("en-GB");
	const pennies = (Math.abs(pence) % 100).toString().padStart(2, "0");
	return `${sign}£${pounds}.${pennies}`;
}

if (!customElements.get("invoice-form")) {
	customElements.define("invoice-form", InvoiceForm);
}
